import { useEffect, useMemo, useState } from "react";

const money = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);

function AnimatedMoney({ value, color }) {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    const start = performance.now();
    const from = shown;
    const duration = 420;
    let frame;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setShown(from + (value - from) * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // Value changes are intentionally the animation trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <div className="animated-number" style={{ color }}>{money(shown)}</div>;
}

function SliderField({ label, hint, value, onChange, min, max, step = 1, prefix = "", suffix = "" }) {
  const percent = ((Number(value) - min) / (max - min)) * 100;
  return <label className="calculator-field">
    <span className="calculator-label">{label}</span>
    <span className="calculator-hint">{hint}</span>
    <div className="slider-value">{prefix}{Number(value).toLocaleString()}{suffix}</div>
    <input className="range-input" type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ "--range-progress": `${percent}%` }} />
    <div className="slider-ends"><span>{prefix}{min.toLocaleString()}{suffix}</span><span>{prefix}{max.toLocaleString()}{suffix}</span></div>
  </label>;
}

function GrowthChart({ data, years }) {
  const width = 720, height = 250, pad = { top: 18, right: 18, bottom: 34, left: 58 };
  const max = Math.max(...data.map((p) => p.balance), 1);
  const x = (year) => pad.left + (year / years) * (width - pad.left - pad.right);
  const y = (value) => height - pad.bottom - (value / max) * (height - pad.top - pad.bottom);
  const line = data.map((p, index) => `${index ? "L" : "M"}${x(p.year).toFixed(1)},${y(p.balance).toFixed(1)}`).join(" ");
  const area = `${line} L${x(years)},${height - pad.bottom} L${x(0)},${height - pad.bottom} Z`;
  return <div className="growth-chart" aria-label="Chart showing your projected balance growing over time">
    <div className="chart-heading"><div><span className="chart-dot" />Projected balance</div><span>Year {years}</span></div>
    <svg viewBox={`0 0 ${width} ${height}`} role="img">
      <defs><linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1"><stop stopColor="#0f8a6b" stopOpacity=".28"/><stop offset="1" stopColor="#0f8a6b" stopOpacity="0"/></linearGradient></defs>
      {[0, .25, .5, .75, 1].map((v) => <g key={v}><line x1={pad.left} x2={width - pad.right} y1={y(max * v)} y2={y(max * v)} stroke="var(--line)" strokeDasharray="3 5"/><text x={pad.left - 8} y={y(max * v) + 4} textAnchor="end" className="chart-axis">{money(max * v)}</text></g>)}
      <path d={area} fill="url(#growthFill)"/><path d={line} fill="none" stroke="var(--emerald)" strokeWidth="4" strokeLinecap="round" className="chart-line"/>
      {data.filter((p) => p.year === 0 || p.year === years || p.year % Math.max(1, Math.ceil(years / 4)) === 0).map((p) => <g key={p.year}><circle cx={x(p.year)} cy={y(p.balance)} r="4" fill="var(--bg)" stroke="var(--emerald)" strokeWidth="3"/><text x={x(p.year)} y={height - 12} textAnchor="middle" className="chart-axis">{p.year === 0 ? "Today" : `${p.year}y`}</text></g>)}
    </svg>
  </div>;
}

export default function CompoundInterest() {
  const [principal, setPrincipal] = useState(1000);
  const [rate, setRate] = useState(5);
  const [years, setYears] = useState(10);
  const [monthlyDeposit, setMonthlyDeposit] = useState(50);

  const { result, data } = useMemo(() => {
    const monthlyRate = rate / 100 / 12;
    let balance = principal;
    const points = [{ year: 0, balance }];
    for (let month = 1; month <= years * 12; month += 1) {
      balance = balance * (1 + monthlyRate) + monthlyDeposit;
      if (month % 12 === 0) points.push({ year: month / 12, balance });
    }
    const contributed = principal + monthlyDeposit * years * 12;
    return { result: { balance, contributed, interest: balance - contributed }, data: points };
  }, [principal, rate, years, monthlyDeposit]);
  const growthShare = result.balance ? (result.interest / result.balance) * 100 : 0;

  return <main style={{ padding: "120px 0 80px" }}>
    <div className="container calculator-page">
      <div className="calculator-eyebrow">MAKE YOUR FUTURE VISIBLE</div>
      <h1>Compound Interest</h1>
      <p className="calculator-intro">Drag the controls and watch small choices turn into a much bigger number.</p>

      <div className="calculator-layout">
        <section className="card-base calculator-controls">
          <SliderField label="Starting amount" hint="What you already have saved." value={principal} onChange={setPrincipal} min={0} max={20000} step={100} prefix="$" />
          <SliderField label="Interest rate" hint="Use your savings account APY." value={rate} onChange={setRate} min={0} max={12} step={0.1} suffix="%" />
          <SliderField label="Years to grow" hint="How long can this money stay invested?" value={years} onChange={setYears} min={1} max={40} suffix=" years" />
          <SliderField label="Monthly deposit" hint="The habit that makes the curve take off." value={monthlyDeposit} onChange={setMonthlyDeposit} min={0} max={1000} step={25} prefix="$" />
        </section>
        <section className="calculator-results">
          <div className="result-highlight card-base"><div className="result-caption">Your projected balance</div><AnimatedMoney value={result.balance} color="var(--emerald)" /><div className="result-subcopy">in {years} years, if you stay consistent</div></div>
          <GrowthChart data={data} years={years} />
        </section>
      </div>
      <div className="result-stats">
        <div className="card-base"><span>You put in</span><AnimatedMoney value={result.contributed} /></div>
        <div className="card-base"><span>Growth does the rest</span><AnimatedMoney value={result.interest} color="var(--emerald)" /></div>
        <div className="card-base"><span>Powered by growth</span><strong>{growthShare.toFixed(0)}%</strong></div>
      </div>
      <div className="card-base insight-callout"><span>✦</span> Every month you keep this up, the line gets a little steeper. At this pace, <strong>{money(result.interest)}</strong> is money your money earns for you.</div>
    </div>
  </main>;
}
